import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { IReportDataGathererFactory, IReportValueAccessor } from './interfaces';
import { ReportConfigSceneScreenshotValue } from './ReportConfigSceneScreenshotValue';
import { ReportConfigCustomValue } from './ReportConfigCustomValue';
import { ReportConfigDataModelValue } from './ReportConfigDataModelValue';
import { type IReportDataGatherer, type IReportConfig } from './interfaces';
import { injectable, inject } from 'inversify';
import { TYPES } from '@/engine/ioc/types';
import type { ViewsService } from '../../Views.service';
import type { ViewController } from '../../View.controller';
import type { CasePhaseData } from '../../CasePhaseData/CasePhaseData.ts';
import type { ImagesService } from '../images/Images.service';
import { TYPES as IMAGES_TYPES } from '../images/iocTypes';
import { iocContainer } from '@/engine/ioc';
import { PhaseSwitcher } from './PhaseSwitcher';
import { ReportScreenshotBuilder } from './ReportScreenshotBuilder';
import { ReportCanvasScreenshotAccessor } from './ReportCanvasScreenshotAccessor';
import { ReportCustomValueAccessor } from './ReportCustomValueAccessor';
import { ReportDataModelValueAccessor } from './ReportDataModelValueAccessor';
import { ReportDataGatherer } from './ReportDataGatherer';
import type { ConfigEntryConstructor } from './interfaces';
import { ViewSwitcher } from './ViewSwitcher';
import type { AppController } from '../../App.controller';

@injectable()
/**
 * Factory class for creating instances of ReportDataGatherer.
 */
export class ReportDataGathererFactory implements IReportDataGathererFactory {
  private _reportValueAccessorsMap: Map<ConfigEntryConstructor, IReportValueAccessor> = new Map();

  private readonly _viewsService: ViewsService;

  public constructor(
    @inject(TYPES.AppController) appController: AppController,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(IMAGES_TYPES.ImagesService) imagesService: ImagesService
  ) {
    this._viewsService = viewsService;

    const phaseSwitcher = new PhaseSwitcher(appController, viewsService, casePhaseData, imagesService);
    const viewSwitcher = new ViewSwitcher(viewsService);

    this._reportValueAccessorsMap.set(
      ReportConfigDataModelValue,
      new ReportDataModelValueAccessor(planningAlgorithm, phaseSwitcher)
    );
    this._reportValueAccessorsMap.set(
      ReportConfigSceneScreenshotValue,
      new ReportCanvasScreenshotAccessor(
        new ReportScreenshotBuilder(
          viewsService,
          viewController,
          phaseSwitcher,
          viewSwitcher,
          planningAlgorithm,
          imagesService
        )
      )
    );
    this._reportValueAccessorsMap.set(
      ReportConfigCustomValue,
      new ReportCustomValueAccessor(planningAlgorithm, phaseSwitcher, iocContainer)
    );
  }

  public createDataGatherer(config: IReportConfig): IReportDataGatherer {
    return new ReportDataGatherer(config, this._reportValueAccessorsMap, this._viewsService);
  }
}
